import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Users } from 'server/shared/collections/users';
import { DeclineReasonEnum } from 'server/shared/models/declineReason.enum';
import { EmploymentUserLog } from 'server/shared/models/employmentUserLog';
import { EmploymentUserLogTypeEnum } from 'server/shared/models/employmentUserLogType.enum';
import { User } from 'server/shared/models/user';
import { PreparedEmploymentInvitee } from '../../../types/employment/PreparedEmploymentInvitee';

export type PreparedUserLogType = 'neutral' | 'success' | 'danger' | 'warning';

const logTypeData: {
  [key in EmploymentUserLogTypeEnum]: {
    icon: string;
    colorType: PreparedUserLogType;
  };
} = {
  [EmploymentUserLogTypeEnum.SELF_SIGN_IN_AVAILABLE]: {
    icon: 'check',
    colorType: 'success',
  },
  [EmploymentUserLogTypeEnum.INVITED]: {
    icon: 'clock',
    colorType: 'neutral',
  },
  [EmploymentUserLogTypeEnum.INVITEE_AVAILABLE]: {
    icon: 'check',
    colorType: 'success',
  },
  [EmploymentUserLogTypeEnum.INVITEE_UNAVAILABLE]: {
    icon: 'close',
    colorType: 'danger',
  },
  [EmploymentUserLogTypeEnum.SET_TO_AVAILABLE]: {
    icon: 'check',
    colorType: 'success',
  },
  [EmploymentUserLogTypeEnum.SET_TO_UNAVAILABLE]: {
    icon: 'close',
    colorType: 'danger',
  },
  [EmploymentUserLogTypeEnum.DECLINED]: {
    icon: 'close-rejected-remove-circle',
    colorType: 'danger',
  },
  [EmploymentUserLogTypeEnum.CANCELLED]: {
    icon: 'star-rejected-storno-filled',
    colorType: 'danger',
  },
  [EmploymentUserLogTypeEnum.BOOKED]: {
    icon: 'star-filled',
    colorType: 'warning',
  },
  [EmploymentUserLogTypeEnum.POSSIBLE_CANDIDATE]: {
    icon: 'star-check-outline',
    colorType: 'success',
  },
};

export type PreparedEmploymentUserLog = EmploymentUserLog & {
  icon: string;
  colorType: PreparedUserLogType;
  user?: User;
};

@Component({
  selector: 'app-employment-user-logs-bottom-sheet',
  templateUrl: './employment-user-logs-bottom-sheet.component.html',
  styleUrls: ['./employment-user-logs-bottom-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmploymentUserLogsBottomSheetComponent
  implements OnInit, OnDestroy
{
  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA)
    public data: {
      userLogs: EmploymentUserLog[];
      invitee?: PreparedEmploymentInvitee;
    },
    private ngZone: NgZone,
  ) {}

  isLoading$ = new BehaviorSubject<boolean>(true);

  preparedUserLogs$ = new BehaviorSubject<PreparedEmploymentUserLog[]>([]);

  declineReasonEnum = DeclineReasonEnum;

  usersSubscription?: Subscription;
  ngOnInit(): void {
    this.readSheetData();
  }

  readSheetData() {
    if (!this.data.userLogs) {
      throw new Error('No invitee provided for employment userLogs');
    }
    const userLogUserIds = this.data.userLogs.map((log) => log.actionUserId);
    this.usersSubscription = Users.find(() => {
      return { _id: { $in: userLogUserIds } };
    }).subscribe((users) => {
      this.ngZone.run(() => {
        this.preparedUserLogs$.next(
          this.data.userLogs.map((log) => this.prepareUserLog(log, users)),
        );
      });
    });
  }

  prepareUserLog(
    log: EmploymentUserLog,
    users: User[],
  ): PreparedEmploymentUserLog {
    const user = users.find((u) => u._id === log.actionUserId);
    return {
      ...log,
      ...logTypeData[log.type],
      user,
    };
  }

  ngOnDestroy() {
    if (this.usersSubscription) {
      this.usersSubscription.unsubscribe();
    }
  }

  readonly EmploymentUserLogTypeEnum = EmploymentUserLogTypeEnum;
  protected readonly DeclineReasonEnum = DeclineReasonEnum;
}
